/**
 * AttendanceCapturePage
 *
 * Kiosk screen: live camera → capture JPEG → GPS → face-recognize → show result.
 *
 * On mount the page resolves the logged-in user's employee record via
 * getEmployeeByUidx(uidx).  If the account has no linked employee profile the
 * page shows a friendly error instead of crashing.
 */

import React, { useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../../../App";
import AddonGate from "../../../../components/AddonGate";
import { recognizeFace, listAttendance, getEmployeeByUidx } from "../../../../config/Services";
import "./AttendanceCapturePage.css";

/* ── helpers ─────────────────────────────────────────── */
function getOrCreateDeviceId() {
  let id = localStorage.getItem("attendanceDeviceId");
  if (!id) {
    id = "device-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("attendanceDeviceId", id);
  }
  return id;
}

function todayRange() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return { startDate: date, endDate: date };
}

/* ── Icons ───────────────────────────────────────────── */
function BackIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M19 12H5M12 5l-7 7 7 7" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

/* ── Employee-not-found banner ───────────────────────── */
function EmployeeNotFoundBanner({ message, isDarkMode, onRetry }) {
  return (
    <div className={`acp-emp-not-found${isDarkMode ? " dark" : ""}`}>
      <div className="acp-emp-not-found-icon">
        <AlertIcon />
      </div>
      <div className="acp-emp-not-found-body">
        <strong>Employee profile not linked</strong>
        <p>
          {message === "no_employee"
            ? "Your login account is not connected to an employee record in this location. Attendance capture is unavailable. Please contact your HR administrator to link your profile."
            : message}
        </p>
      </div>
      {onRetry && (
        <button className="acp-emp-not-found-retry" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

/* ── Inner component ─────────────────────────────────── */
function AttendanceCaptureContent() {
  const navigate = useNavigate();
  const { isDarkMode } = useContext(UserContext) || {};

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  /* ── Employee preflight ──────────────────────────────── */
  const [empState, setEmpState] = useState({
    loading: true,
    employeeId: null,
    employeeName: null,
    error: null,
  });

  const checkEmployee = useCallback(async () => {
    setEmpState({ loading: true, employeeId: null, employeeName: null, error: null });
    try {
      let retailUser = null;
      try {
        retailUser = JSON.parse(localStorage.getItem("retailUserDetails") || "null");
      } catch (_) {}

      const uidx = retailUser?.uidx;
      if (!uidx) {
        setEmpState({
          loading: false,
          employeeId: null,
          employeeName: null,
          error: "Login session data is missing. Please sign in again.",
        });
        return;
      }

      const res = await getEmployeeByUidx(uidx);
      const d = res?.data?.data?.data;

      if (d?.employeeId) {
        setEmpState({
          loading: false,
          employeeId: d.employeeId,
          employeeName: d.employeeName || null,
          error: null,
        });
      } else {
        setEmpState({ loading: false, employeeId: null, employeeName: null, error: "no_employee" });
      }
    } catch (err) {
      const msg =
        err?.response?.data?.data?.message ||
        err?.response?.data?.message ||
        err?.message ||
        "Could not verify your employee profile. Please try again.";
      setEmpState({ loading: false, employeeId: null, employeeName: null, error: msg });
    }
  }, []);

  useEffect(() => {
    checkEmployee();
  }, [checkEmployee]);

  /* ── Camera ────────────────────────────────────────────── */
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | capturing | success | error
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  /* Start camera once employee is confirmed */
  useEffect(() => {
    if (!empState.employeeId) return; // don't start camera until employee is verified
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            if (!cancelled) setCameraReady(true);
          };
        }
      } catch (err) {
        if (!cancelled) setCameraError("Camera access denied. Please allow camera permissions.");
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [empState.employeeId]);

  /* Capture frame → base64 JPEG */
  const captureFrame = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return null;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext("2d").drawImage(video, 0, 0);
    return canvas.toDataURL("image/jpeg", 0.85);
  }, []);

  /* Get GPS coords */
  const getCoords = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({ latitude: null, longitude: null });
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => resolve({ latitude: null, longitude: null }),
        { timeout: 5000 }
      );
    });

  /* Duplicate-punch guard: check today's list before re-enabling */
  const guardCheck = useCallback(async () => {
    try {
      const locId = localStorage.getItem("locId");
      await listAttendance({
        locationIds: locId ? [locId] : [],
        ...todayRange(),
        page: 0, // 0-based
        size: 20,
        searchInput: "",
        employeeIds: empState.employeeId ? [empState.employeeId] : [],
        attendanceIds: [],
        attendanceStatuses: [],
        shiftTypes: [],
        verificationModes: [],
      });
    } catch (_) {
      /* ignore — this is a guard call only */
    }
    setPhase("idle");
  }, [empState.employeeId]);

  /* Handle capture button click */
  const handleCapture = useCallback(async () => {
    if (phase !== "idle") return;
    setPhase("capturing");
    setResult(null);
    setErrorMsg("");

    const imageDataUrl = captureFrame();
    if (!imageDataUrl) {
      setErrorMsg("Could not capture frame from camera.");
      setPhase("idle");
      return;
    }

    const coords = await getCoords();
    const orgId = localStorage.getItem("orgId");
    const locId = localStorage.getItem("locId");
    const locName = localStorage.getItem("locationName") || localStorage.getItem("locName") || null;
    const deviceId = getOrCreateDeviceId();

    try {
      const res = await recognizeFace({
        imageBase64: imageDataUrl,
        latitude: coords.latitude,
        longitude: coords.longitude,
        organizationId: orgId,
        locationId: locId,
        locationName: locName,
        deviceId,
        deviceName: null,
        deviceType: "mobile",
        challengeToken: null,
        livenessFrames: null,
        employeeId: null,
        createdBy: "ADMIN",
        updatedBy: "ADMIN",
        createdByName: "Admin User",
        updatedByName: "Admin User",
      });

      /* Response: { es, message, statusCode, data: { action, employeeName, ... } } */
      const data = res?.data?.data ?? {};
      if (res?.data?.es === 0) {
        setResult(data);
        setPhase("success");
        // Reset after 8 s so staff can punch again
        setTimeout(() => {
          setPhase("idle");
          setResult(null);
        }, 8000);
      } else {
        const msg = res?.data?.message || "Recognition failed.";
        setErrorMsg(msg);
        await guardCheck();
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.data?.message ||
        "Network error. Please try again.";
      setErrorMsg(msg);
      await guardCheck();
    }
  }, [phase, captureFrame, guardCheck]);

  /* ── Render ──────────────────────────────────────────── */
  return (
    <div className={`acp-root${isDarkMode ? " dark" : ""}`}>
      {/* Header */}
      <div className="acp-header">
        <button
          className="acp-back-btn"
          onClick={() => navigate("/products/staff-management")}
          aria-label="Back to Staff Management"
        >
          <BackIcon />
        </button>
        <div className="acp-header-text">
          <h1>Attendance Capture</h1>
          <p>
            {empState.employeeName
              ? `Logged in as ${empState.employeeName}`
              : "Face recognition clock-in / clock-out"}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="acp-body">
        {/* Employee preflight — loading skeleton */}
        {empState.loading && (
          <div className="acp-preflight-loading">
            <div className="acp-spinner" />
            <p>Verifying employee profile…</p>
          </div>
        )}

        {/* Employee preflight — error */}
        {!empState.loading && empState.error && (
          <EmployeeNotFoundBanner
            message={empState.error}
            isDarkMode={isDarkMode}
            onRetry={empState.error !== "no_employee" ? checkEmployee : null}
          />
        )}

        {/* Camera card — only when employee is verified */}
        {!empState.loading && !empState.error && (
          <div className="acp-camera-card">
            {/* Camera feed */}
            <div className="acp-video-wrap">
              {cameraError ? (
                <div className="acp-camera-error">
                  <CameraIcon />
                  <p>{cameraError}</p>
                </div>
              ) : (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className={`acp-video${cameraReady ? " ready" : ""}`}
                  />
                  {!cameraReady && (
                    <div className="acp-video-loading">
                      <div className="acp-spinner" />
                      <p>Starting camera…</p>
                    </div>
                  )}
                </>
              )}
              {/* Face guide overlay */}
              {cameraReady && <div className="acp-face-guide" aria-hidden="true" />}
            </div>

            {/* Hidden canvas for capture */}
            <canvas ref={canvasRef} style={{ display: "none" }} />

            {/* Status / result */}
            {phase === "capturing" && (
              <div className="acp-status">
                <div className="acp-spinner" />
                <span>Recognising…</span>
              </div>
            )}

            {phase === "success" && result && (
              <div className="acp-result-popup">
                {/* Action badge */}
                <span
                  className={`acp-action-badge ${result.action === "punched_out" ? "out" : "in"}`}
                >
                  {result.action === "punched_out" ? "✓ Punched Out" : "✓ Punched In"}
                </span>

                {/* Employee name */}
                <div className="acp-result-name">{result.employeeName || "—"}</div>
                {result.employeeId && <div className="acp-result-empid">{result.employeeId}</div>}

                {/* Confidence */}
                {result.confidence != null && (
                  <div className="acp-result-confidence">
                    Face match: <strong>{parseFloat(result.confidence).toFixed(1)}%</strong>
                  </div>
                )}

                {/* Swipe times */}
                <div className="acp-result-times">
                  {result.swipeIn && <span className="acp-time-pill in">In {result.swipeIn}</span>}
                  {result.swipeOut && (
                    <span className="acp-time-pill out">Out {result.swipeOut}</span>
                  )}
                </div>

                {/* Late warning */}
                {result.isLate && result.lateByMinutes > 0 && (
                  <div className="acp-result-late">
                    ⚠ Late by{" "}
                    {result.lateByMinutes >= 60
                      ? `${Math.floor(result.lateByMinutes / 60)}hr ${result.lateByMinutes % 60}min`
                      : `${result.lateByMinutes}min`}
                  </div>
                )}

                {/* Location */}
                {result.locationName && (
                  <div className="acp-result-location">📍 {result.locationName}</div>
                )}
              </div>
            )}

            {errorMsg && phase === "idle" && (
              <div className="acp-result error">
                <p>{errorMsg}</p>
              </div>
            )}

            {/* Capture button */}
            <button
              className="acp-capture-btn"
              onClick={handleCapture}
              disabled={!cameraReady || phase === "capturing"}
            >
              <CameraIcon />
              {phase === "capturing" ? "Processing…" : "Capture"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Default export — wrapped in AddonGate ────────────── */
export default function AttendanceCapturePage() {
  return (
    <AddonGate addonName="pallet workforce">
      <AttendanceCaptureContent />
    </AddonGate>
  );
}
